import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Spinner,
  Center,
  Icon,
  Collapse,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  FaBox,
  FaMapMarkerAlt,
  FaBolt,
  FaExclamationTriangle,
  FaClock,
  FaUsers,
} from "react-icons/fa";
import NestedEntityCard from "./NestedEntityCard";
import {
  getDispatchOrdersForSupplier,
  getDispatchSupplierFacets,
  getDispatchCustomerFacets,
  mapOrderToComponentFormat,
} from "../services";
import EngagementModeBadge from "../../../components/EngagementModeBadge";
import { IoMdPricetag } from "react-icons/io";
import { RiStackLine } from "react-icons/ri";
import { buildAddress } from "../../../utils/util";
import { GaraazTextComponent } from "@svan-garaaz/reactsharedlib";
import {
  findLatestETAWithGivenTime,
  formatETADateTime,
  sortByDate,
} from "../../../utils/dateResolvers";
import { ClientId, DeliveryModes } from "../../../types";
import { OrdersCard } from "./OrdersCard";
import { getGlobalApiConfig } from "../../../config/ApiConfig";
import { GroupingMode } from "../types";
import type { EntityData, OrderData, GroupCardProps } from "../types";

export default function GroupCard({
  entityName,
  entityId,
  groupingMode,
  expanded,
  onToggleExpansion,
  isLoading = false,
  entityFacet,
  selectedOrders = [],
  onOrderSelectionChange,
  activeTab,
  collapseSignal,
}: GroupCardProps) {
  const toast = useToast();
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(
    new Set()
  );
  const [childEntities, setChildEntities] = useState<EntityData[]>([]);
  const [childrenLoading, setChildrenLoading] = useState(false);

  // For SUPPLIER_APP: directly show orders without nesting
  const { clientId } = getGlobalApiConfig() ?? { clientId: undefined };
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const isCustomerGrouping = groupingMode === GroupingMode.CUSTOMER;
  const isSupplierApp = clientId === ClientId.SUPPLIER_APP;
  const loggedInSupplier = childEntities?.[0];

  // Determine entity labels based on grouping mode
  const childEntityLabel = isCustomerGrouping ? "suppliers" : "customers";
  const childEntitySingular = isCustomerGrouping ? "Supplier" : "Customer";

  // Get display values from facet (works for both customer and supplier facets)
  const displayName = entityFacet?.name || entityFacet?.businessName || entityName;
  const orderCount = entityFacet?.count || 0;
  const urgentCount = entityFacet?.urgentOrdersCount || 0;
  const facetTotalItems = entityFacet?.totalItemsCount;
  const facetTotalQuantity = entityFacet?.totalOrderItemsQuantity;
  const facetTotalValue = entityFacet?.totalInvoiceAmount;

  // Get address based on grouping mode
  const entityAddress = useMemo(() => {
    const address = isCustomerGrouping
      ? entityFacet?.shipmentAddress
      : entityFacet?.supplierAddress;
    
    if (!address?.addressLine1) return null;

    return buildAddress({
      firstLine: address.addressLine1,
      secondLine: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      pincode: "",
    });
  }, [entityFacet, isCustomerGrouping]);

  // Calculate totals (from facet or aggregate from children)
  const totalItems = (() => {
    if (facetTotalItems !== undefined && facetTotalItems !== null && !isNaN(facetTotalItems)) {
      return facetTotalItems;
    }
    return childEntities.reduce(
      (sum, entity) => sum + (entity.totalItemsCount || entity.totalItems || 0),
      0
    );
  })();

  const totalQuantity = (() => {
    if (facetTotalQuantity !== undefined && facetTotalQuantity !== null && !isNaN(facetTotalQuantity)) {
      return facetTotalQuantity;
    }
    return childEntities.reduce(
      (sum, entity) => sum + (entity.totalOrderItemsQuantity || entity.totalQuantity || 0),
      0
    );
  })();

  const totalValue = (() => {
    if (facetTotalValue !== undefined && facetTotalValue !== null && !isNaN(facetTotalValue)) {
      return parseFloat(facetTotalValue.toString()) || 0;
    }
    return childEntities.reduce((sum, entity) => {
      const value = parseFloat((entity.totalInvoiceAmount || entity.totalValue)?.toString() || "0");
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  })();

  // Sort orders for direct display (SUPPLIER_APP case)
  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const etaA = findLatestETAWithGivenTime(a?.orderETA ?? []);
      const etaB = findLatestETAWithGivenTime(b?.orderETA ?? []);

      return sortByDate(
        a,
        b,
        () => etaA?.latestTime,
        () => etaB?.latestTime
      );
    });
  }, [orders]);

  // Fetch orders directly for SUPPLIER_APP
  const fetchOrders = useCallback(async () => {
    if (!entityFacet?._id || !loggedInSupplier?._id || orders.length > 0) return;

    setOrdersLoading(true);
    try {
      const response = await getDispatchOrdersForSupplier(
        entityFacet._id,
        loggedInSupplier._id,
        activeTab
      );
      const apiOrders = response.orders || [];

      const mappedOrders: OrderData[] = apiOrders.map(mapOrderToComponentFormat);
      setOrders(mappedOrders);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMsg ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch orders";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setOrdersLoading(false);
    }
  }, [entityFacet?._id, loggedInSupplier?._id, orders.length, toast, activeTab]);

  useEffect(() => {
    if (expanded && orders.length === 0 && isCustomerGrouping && isSupplierApp && loggedInSupplier?._id) {
      fetchOrders();
    }
  }, [expanded, orders.length, fetchOrders, isCustomerGrouping, isSupplierApp, loggedInSupplier?._id]);

  const fetchChildEntities = useCallback(async () => {
    if (!entityFacet?._id || childEntities.length > 0) return;
    setChildrenLoading(true);
    try {
      let response;
      if (isCustomerGrouping) {
        response = await getDispatchSupplierFacets(entityFacet._id, {}, activeTab);
        const supplierFacets = response.suppliers || [];

        const mappedEntities: EntityData[] = supplierFacets.map(
          (supplier: any) => ({
            _id: supplier._id,
            name: supplier.businessName || supplier.name || "Unknown Supplier",
            businessName: supplier.businessName || supplier.name || "Unknown Supplier",
            address: supplier.supplierAddress,
            supplierAddress: supplier.supplierAddress,
            totalItems: supplier.totalItemsCount || 0,
            totalItemsCount: supplier.totalItemsCount || 0,
            totalQuantity: supplier.totalOrderItemsQuantity || 0,
            totalOrderItemsQuantity: supplier.totalOrderItemsQuantity || 0,
            totalValue: parseFloat(supplier.totalInvoiceAmount?.toString() || "0"),
            totalInvoiceAmount: supplier.totalInvoiceAmount,
            count: supplier.count || 0,
            orderCount: supplier.count || 0,
            urgentCount: supplier.urgentOrdersCount || 0,
            urgentOrdersCount: supplier.urgentOrdersCount || 0,
            minETAdate: supplier.minETAdate,
          })
        );
        setChildEntities(sortEntitiesByETA(mappedEntities));
      } else {
        // Supplier grouping: fetch customers for this supplier
        response = await getDispatchCustomerFacets({}, activeTab, entityFacet._id);
        const customerFacets = response.customers || [];

        const mappedEntities: EntityData[] = customerFacets.map(
          (customer: any) => ({
            _id: customer._id,
            name: customer.name || "Unknown Customer",
            businessName: customer.name,
            shipmentAddress: customer.shipmentAddress,
            totalItems: customer.totalItemsCount || 0,
            totalItemsCount: customer.totalItemsCount || 0,
            totalQuantity: customer.totalOrderItemsQuantity || 0,
            totalOrderItemsQuantity: customer.totalOrderItemsQuantity || 0,
            totalValue: parseFloat(customer.totalInvoiceAmount?.toString() || "0"),
            totalInvoiceAmount: customer.totalInvoiceAmount,
            count: customer.count || 0,
            orderCount: customer.count || 0,
            urgentCount: customer.urgentOrdersCount || 0,
            urgentOrdersCount: customer.urgentOrdersCount || 0,
            minETAdate: customer.minETAdate,
            engagementMode: customer.engagementMode,
          })
        );
        setChildEntities(sortEntitiesByETA(mappedEntities));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            `Failed to fetch ${childEntityLabel} for this ${isCustomerGrouping ? "customer" : "supplier"}`;

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChildrenLoading(false);
    }
  }, [entityFacet?._id, childEntities.length, toast, activeTab, isCustomerGrouping, childEntityLabel]);

  // Helper to sort entities by ETA
  const sortEntitiesByETA = (entities: EntityData[]) => {
    return entities.sort((a, b) => {
      return sortByDate(
        a,
        b,
        () => a.minETAdate,
        () => b.minETAdate
      );
    });
  };

  useEffect(() => {
    if (expanded && childEntities.length === 0) {
      fetchChildEntities();
    }
  }, [expanded, childEntities.length, fetchChildEntities]);

  useEffect(() => {
    if (collapseSignal && collapseSignal > 0) {
      setExpandedChildren(new Set());
      setChildEntities([]);
      setOrders([]);
    }
  }, [collapseSignal]);

  const toggleChildExpansion = (childId: string) => {
    setExpandedChildren((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  };

  // Style variables based on grouping mode
  const headerGradient = isCustomerGrouping
    ? "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)"
    : "linear-gradient(135deg, #f8f7ff 0%, #ede9fe 50%, #ddd6fe 100%)";
  const accentColor = isCustomerGrouping ? "blue.500" : "purple.500";
  const spinnerColor = isCustomerGrouping ? "blue.500" : "purple.500";

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      shadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px"
      borderColor="gray.100"
      overflow="hidden"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        shadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        transform: "translateY(-2px)",
      }}
      mb={3}
    >
      <Box
        bg={headerGradient}
        p={6}
        position="relative"
        overflow="hidden"
        borderBottom="1px"
        borderColor="gray.100"
        onClick={onToggleExpansion}
        cursor={"pointer"}
      >
        <Box
          position="absolute"
          top="-100px"
          right="-100px"
          w="300px"
          h="300px"
          opacity={0.03}
          bg={isCustomerGrouping 
            ? "linear-gradient(135deg, #64748b 0%, #475569 100%)"
            : "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
          }
          borderRadius="full"
          transform="translate(50px, -50px)"
        />
        <Box
          position="absolute"
          bottom="-80px"
          left="-80px"
          w="200px"
          h="200px"
          opacity={0.02}
          bg={isCustomerGrouping
            ? "linear-gradient(135deg, #475569 0%, #334155 100%)"
            : "linear-gradient(135deg, #5b21b6 0%, #4c1d95 100%)"
          }
          borderRadius="full"
          transform="translate(-50px, 50px)"
        />

        <VStack align="stretch" spacing={6} position="relative" zIndex={1}>
          <HStack justify="space-between" align="flex-start">
            <VStack align="start" spacing={3} flex={1}>
              <VStack align="start" spacing={1.5}>
                <HStack spacing={3}>
                  <Heading
                    size="md"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="-0.025em"
                    lineHeight="1.2"
                  >
                    {displayName}
                  </Heading>
                  <HStack spacing={3}>
                    <Badge
                      bg="white"
                      color="gray.700"
                      variant="subtle"
                      px={3}
                      py={0.5}
                      borderRadius="full"
                      fontWeight="600"
                      border="1px"
                      borderColor="gray.200"
                      lineHeight={"normal"}
                      textTransform={"none"}
                    >
                      {orderCount} Orders
                    </Badge>
                    {urgentCount > 0 && (
                      <Badge
                        bg="orange.50"
                        color="orange.700"
                        variant="subtle"
                        px={3}
                        py={0.5}
                        borderRadius="full"
                        fontWeight="600"
                        shadow="0 2px 4px rgba(0, 0, 0, 0.06)"
                        border="1px"
                        borderColor="orange.200"
                        lineHeight={"normal"}
                        textTransform={"none"}
                      >
                        <HStack spacing={1.5}>
                          <Icon as={FaBolt} boxSize={3} color="orange.500" />
                          <Text fontSize="xs" fontWeight="600">
                            {urgentCount} Urgent
                          </Text>
                        </HStack>
                      </Badge>
                    )}

                    {entityFacet?.engagementMode && (
                      <EngagementModeBadge
                        engagementMode={entityFacet.engagementMode}
                        size="sm"
                      />
                    )}
                  </HStack>
                </HStack>
                {entityAddress ? (
                  <HStack spacing={2} color="gray.600">
                    <Icon as={FaMapMarkerAlt} boxSize={3} color={accentColor} />
                    <GaraazTextComponent
                      header=""
                      content={entityAddress}
                      maxNoOfLines={3}
                      contentStyle={{
                        width: "400px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "gray.700",
                        cursor: "pointer",
                      }}
                    />
                  </HStack>
                ) : isCustomerGrouping ? (
                  <HStack spacing={2} color="red.500">
                    <Icon as={FaExclamationTriangle} boxSize={3} />
                    <Text fontSize="sm" fontWeight="500">
                      Delivery address required
                    </Text>
                  </HStack>
                ) : null}
              </VStack>
            </VStack>

            <HStack spacing={4} align="center">
              <HStack spacing={8} align="center">
                <VStack spacing={1} align="center" width={"200px"}>
                  <HStack spacing={2} align="center">
                    <Icon as={FaClock} color="orange.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Customer Promise Time
                    </Text>
                  </HStack>
                  <Text fontSize="xl" fontWeight="700" color="gray.800">
                    {formatETADateTime(entityFacet?.minETAdate)}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={2} align="center">
                    <Icon as={FaBox} color="blue.500" boxSize={3.5} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Items
                    </Text>
                  </HStack>
                  <Text fontSize="xl" fontWeight="700" color="gray.800">
                    {isNaN(totalItems) ? 0 : totalItems}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={2} align="center">
                    <Icon as={RiStackLine} color="green.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Qty
                    </Text>
                  </HStack>
                  <Text fontSize="xl" fontWeight="700" color="gray.800">
                    {isNaN(totalQuantity) ? 0 : totalQuantity}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={2} align="center">
                    <Icon as={IoMdPricetag} color="purple.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Value
                    </Text>
                  </HStack>
                  <Text fontSize="xl" fontWeight="700" color="gray.800">
                    ₹{(isNaN(totalValue) ? 0 : totalValue).toLocaleString()}
                  </Text>
                </VStack>
              </HStack>

              <IconButton
                aria-label="Toggle expansion"
                icon={
                  expanded ? (
                    <ChevronUpIcon boxSize={8} />
                  ) : (
                    <ChevronDownIcon boxSize={8} />
                  )
                }
                variant="ghost"
                size="lg"
                _hover={{
                  bg: "none",
                }}
              />
            </HStack>
          </HStack>

          {isLoading && (
            <HStack
              spacing={3}
              justify="center"
              bg="white"
              p={4}
              borderRadius="xl"
              shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
            >
              <Spinner size="sm" color={spinnerColor} />
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Loading orders...
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      {/* For SUPPLIER_APP in customer grouping mode: show orders directly */}
      {isCustomerGrouping && isSupplierApp ? (
        <Collapse in={expanded}>
          <OrdersCard
            sortedOrders={sortedOrders}
            onOrderSelectionChange={onOrderSelectionChange}
            selectedOrders={selectedOrders}
            supplier={loggedInSupplier}
            customerId={entityFacet?._id}
            ordersLoading={ordersLoading}
            orders={orders}
          />
        </Collapse>
      ) : (
        <Collapse in={expanded}>
          <Box p={6} bg="gray.50">
            {childrenLoading ? (
              <Center py={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" color={spinnerColor} />
                  <Text color="gray.600" fontSize="sm">
                    Loading {childEntityLabel} for {displayName}...
                  </Text>
                </VStack>
              </Center>
            ) : childEntities.length === 0 ? (
              <Center py={8}>
                <VStack spacing={3}>
                  <Box p={4} bg="gray.100" borderRadius="full">
                    <Icon as={isCustomerGrouping ? FaBox : FaUsers} boxSize={6} color="gray.400" />
                  </Box>
                  <Text color="gray.500" fontSize="sm">
                    No {childEntityLabel} found for this {isCustomerGrouping ? "customer" : "supplier"}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={4} align="stretch">
                {childEntities.map((childEntity) => {
                  // Determine child entity type (opposite of parent)
                  const childEntityType = isCustomerGrouping
                    ? GroupingMode.SUPPLIER
                    : GroupingMode.CUSTOMER;

                  return (
                    <NestedEntityCard
                      key={childEntity._id}
                      entity={childEntity}
                      entityType={childEntityType}
                      parentEntityId={entityFacet?._id || entityId}
                      parentEntityName={displayName}
                      orders={[]}
                      expanded={expandedChildren.has(childEntity._id)}
                      onToggleExpansion={() => toggleChildExpansion(childEntity._id)}
                      isLoading={false}
                      selectedOrders={selectedOrders}
                      activeTab={activeTab}
                      collapseSignal={collapseSignal}
                      onOrderSelectionChange={(
                        orderNo: string,
                        isSelected: boolean,
                        customerId?: string,
                        customerName?: string,
                        supplierId?: string,
                        supplierName?: string,
                        deliveryMode?: DeliveryModes,
                        orderId?: string,
                        orderData?: any
                      ) =>
                        onOrderSelectionChange?.(
                          orderNo,
                          isSelected,
                          customerId,
                          customerName,
                          supplierId,
                          supplierName,
                          deliveryMode,
                          orderId,
                          orderData
                        )
                      }
                    />
                  );
                })}
              </VStack>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

// Backward compatibility exports
export { GroupCard as CustomerGroupCard };

